import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { User } from '@/lib/types';
import { getMe, login as apiLogin, register as apiRegister, clearToken, claimPaste } from '@/lib/api';
import { getToken } from '@/lib/auth';
import { getOwnedPasteIds, getCreatorToken, getEditCode } from '@/lib/collab';
import { AuthModal } from './AuthModal';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, username: string, password: string) => Promise<void>;
  logout: () => void;
  openAuthModal: (tab?: 'login' | 'register') => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  openAuthModal: () => {},
});

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

/** Best-effort: attach every locally-owned anonymous paste to the account. */
function sweepClaims(): void {
  for (const id of getOwnedPasteIds()) {
    claimPaste(id, {
      creatorToken: getCreatorToken(id) ?? undefined,
      editCode: getEditCode(id) ?? undefined,
    }).catch(() => { /* already owned / expired / offline — ignore */ });
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalTab, setModalTab] = useState<'login' | 'register' | null>(null);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    getMe()
      .then(({ user: me }) => setUser(me))
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { user: me } = await apiLogin(email, password);
    setUser(me);
    sweepClaims();
  }, []);

  const register = useCallback(async (email: string, username: string, password: string) => {
    const { user: me } = await apiRegister(email, username, password);
    setUser(me);
    sweepClaims();
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  const openAuthModal = useCallback((tab: 'login' | 'register' = 'login') => {
    setModalTab(tab);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, openAuthModal }}>
      {children}
      {modalTab && <AuthModal initialTab={modalTab} onClose={() => setModalTab(null)} />}
    </AuthContext.Provider>
  );
}
